import { useAppDispatch } from "../store"
import { useSnackbar } from "notistack"
import { unknownErrorToString } from "../utils/errors"
import { useCallback } from "react"

type SuccessResult<T> = { success: true; data: T }
type FailureResult = { success: false; error: string }

const Result = {
  ok<T>(data: T): SuccessResult<T> {
    return { success: true, data }
  },
  fail(error: string): FailureResult {
    return { success: false, error }
  },
}

/**
 * @returns redux dispatch function that wraps dispatch calls in try/catch and shows error snackbar on failure
 */
export const useSafeDispatch = () => {
  const dispatch = useAppDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const safeDispatch = useCallback(
    async <T>(...dispatchArgs: Parameters<typeof dispatch>) => {
      try {
        const action = dispatchArgs[0]
        const dispatched = dispatch(action)

        if (typeof dispatched === "object" && dispatched && "unwrap" in dispatched) {
          if (typeof dispatched.unwrap === "function") {
            return Result.ok((await dispatched.unwrap()) as T)
          }
        }
        return Result.ok((await dispatched) as T)
      } catch (error) {
        const errorMessage = unknownErrorToString(error)
        enqueueSnackbar(errorMessage, { variant: "error" })
        return Result.fail(errorMessage)
      }
    },
    [dispatch, enqueueSnackbar]
  )

  return safeDispatch
}
